import * as api from "./client.js";

export const TESTIMONIAL_STATUS_TARGETS = ["APPROVED", "REJECTED", "SUSPENDED"] as const;

export type TestimonialStatusTarget = (typeof TESTIMONIAL_STATUS_TARGETS)[number];

export type TestimonialStatusRow = {
  testimonialId: string;
  status: TestimonialStatusTarget;
};

export const TESTIMONIAL_STATUS_MAX_ROWS_PER_CALL = 1000;

export type BatchUpdateTestimonialStatusRequest = {
  operationAt: string;
  dryRun: boolean;
  rows: TestimonialStatusRow[];
};

export type BatchUpdateTestimonialStatusResponse = {
  received: number;
  notFound: number;
  unchanged: number;
  withdrawnSkipped: number;
  notSuspendable: number;
  updatedTestimonials: number;
  testimonialOnly: number;
  updatedProcessingItems: number;
  updatedApprovalRequests: number;
  notFoundIds: string[];
  withdrawnSkippedIds: string[];
  notSuspendableIds: string[];
  dryRun: boolean;
};

export const MOVE_TO_SET_TYPES = ["set_testimonial", "clerk_testimonial"] as const;

export type MoveToSetType = (typeof MOVE_TO_SET_TYPES)[number];

export const MOVE_TO_SET_OUTCOMES = ["moved", "unchanged", "not_found", "failed"] as const;

export type MoveToSetOutcome = (typeof MOVE_TO_SET_OUTCOMES)[number];

export const MOVE_TO_SET_FAMILY_MATE_OUTCOMES = ["moved", "unchanged", "failed"] as const;

export type MoveToSetFamilyMateOutcome = (typeof MOVE_TO_SET_FAMILY_MATE_OUTCOMES)[number];

export const MOVE_TO_SET_MAX_IDS_PER_CALL = 500;

export const APPROVAL_SYNC_FAILED_CODE = "APPROVAL_SYNC_FAILED";

export type MoveToSetTarget = {
  testimonialId: string;
  testimonialType: MoveToSetType;
};

export type MoveTestimonialsToSetRequest = {
  ids: string[];
  testimonialType: MoveToSetType;
  dryRun: boolean;
};

export type MoveToSetResult = {
  id: string;
  outcome: MoveToSetOutcome;
  previousContactId: string | null;
  previousTestimonialType: string | null;
  error?: string;
};

export type MoveToSetFamilyMate = {
  id: string;
  requestedId: string;
  outcome: MoveToSetFamilyMateOutcome;
  previousContactId: string | null;
  previousTestimonialType: string | null;
  error?: string;
};

export type MoveTestimonialsToSetResponse = {
  dryRun: boolean;
  testimonialType: MoveToSetType;
  totals: {
    requested: number;
    moved: number;
    unchanged: number;
    notFound: number;
    failed: number;
    familyMatesMoved: number;
  };
  results: MoveToSetResult[];
  familyMates: MoveToSetFamilyMate[];
  approvalSync: { externalIds: number; batches: number; failedBatches: number } | null;
  auditEmitFailed: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): boolean {
  return value === null || typeof value === "string";
}

function hasOutcomeShape(value: Record<string, unknown>, outcomes: readonly string[]): boolean {
  return (
    typeof value.id === "string" &&
    (outcomes as readonly unknown[]).includes(value.outcome) &&
    isNullableString(value.previousContactId) &&
    isNullableString(value.previousTestimonialType) &&
    (value.error === undefined || typeof value.error === "string")
  );
}

function isMoveToSetResult(value: unknown): value is MoveToSetResult {
  return isRecord(value) && hasOutcomeShape(value, MOVE_TO_SET_OUTCOMES);
}

function isMoveToSetFamilyMate(value: unknown): value is MoveToSetFamilyMate {
  return (
    isRecord(value) &&
    hasOutcomeShape(value, MOVE_TO_SET_FAMILY_MATE_OUTCOMES) &&
    typeof value.requestedId === "string"
  );
}

function isMoveTestimonialsToSetResponse(value: unknown): value is MoveTestimonialsToSetResponse {
  return (
    isRecord(value) &&
    (MOVE_TO_SET_TYPES as readonly unknown[]).includes(value.testimonialType) &&
    Array.isArray(value.results) &&
    value.results.every(isMoveToSetResult) &&
    Array.isArray(value.familyMates) &&
    value.familyMates.every(isMoveToSetFamilyMate) &&
    typeof value.auditEmitFailed === "boolean"
  );
}

export function approvalSyncFailureReport(err: unknown): MoveTestimonialsToSetResponse | null {
  if (!(err instanceof Error)) return null;
  const { status, code, detailsRaw } = err as Error & {
    status?: unknown;
    code?: unknown;
    detailsRaw?: unknown;
  };
  if (status !== 503 || code !== APPROVAL_SYNC_FAILED_CODE) return null;
  return isMoveTestimonialsToSetResponse(detailsRaw) ? detailsRaw : null;
}

export const pipelineBatchUpdatesService = {
  async batchUpdateTestimonialStatus(
    body: BatchUpdateTestimonialStatusRequest,
  ): Promise<BatchUpdateTestimonialStatusResponse> {
    return api.post("/v1/content-processor/developer/batch-updates/testimonial-status", body);
  },

  async moveTestimonialsToSet(
    body: MoveTestimonialsToSetRequest,
  ): Promise<MoveTestimonialsToSetResponse> {
    return api.post("/v1/content-processor/staff/testimonials/bulk/move-to-set", body);
  },
};
